import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  Alert,
  IconButton,
  Tooltip,
  Card,
  CardContent,
  Stack,
  Divider,
  Avatar,
  useTheme,
  alpha,
  useMediaQuery,
  Fade,
  Zoom,
  Skeleton,
} from '@mui/material';
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Visibility as ViewIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationOnIcon,
  Schedule as ScheduleIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { collection, query, where, getDocs, doc, updateDoc, orderBy } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../contexts/AuthContext';

const AdminApprovals = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const { isSuperAdmin } = useAuth();
  const [applications, setApplications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedApp, setSelectedApp] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) {
      fetchApplications();
    }
  }, [isSuperAdmin]);

  const fetchApplications = async () => {
    setLoading(true);
    setError('');
    try {
      const q = query(collection(db, 'users'), where('role', '==', 'pending'), orderBy('createdAt', 'desc'));
      const querySnapshot = await getDocs(q);
      const apps = [];
      for (const docSnap of querySnapshot.docs) {
        const userData = docSnap.data();
        apps.push({
          id: docSnap.id,
          ...userData,
        });
      }
      setApplications(apps);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async (application) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateDoc(doc(db, 'users', application.id), {
        role: 'admin',
        isApproved: true,
        applicationStatus: 'approved',
        approvedAt: new Date().toISOString(),
        approvedBy: isSuperAdmin ? 'super_admin' : 'admin',
      });
      
      setSuccess(`Application from ${application.displayName} has been approved`);
      fetchApplications();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error approving application:', error);
      setError('Failed to approve application');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async (application) => {
    setActionLoading(true);
    setError('');
    setSuccess('');
    
    try {
      await updateDoc(doc(db, 'users', application.id), {
        role: 'rejected',
        isApproved: false,
        applicationStatus: 'rejected',
        rejectedAt: new Date().toISOString(),
        rejectedBy: isSuperAdmin ? 'super_admin' : 'admin',
      });
      
      setSuccess(`Application from ${application.displayName} has been rejected`);
      fetchApplications();
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Error rejecting application:', error);
      setError('Failed to reject application');
      setTimeout(() => setError(''), 3000);
    } finally {
      setActionLoading(false);
    }
  };

  const viewDetails = (application) => {
    setSelectedApp(application);
    setDialogOpen(true);
  };

  const stats = {
    total: applications.length,
    pending: applications.length,
  };

  // Mobile Application Card Component
  const ApplicationCard = ({ app, index }) => {
    const [expanded, setExpanded] = useState(false);
    
    return (
      <Zoom in style={{ transitionDelay: `${index * 50}ms` }}>
        <Card sx={{ 
          mb: 2, 
          borderRadius: 2,
          overflow: 'hidden',
        }}>
          <CardContent sx={{ p: 2 }}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar 
                sx={{ 
                  bgcolor: theme.palette.warning.main,
                  width: 50,
                  height: 50,
                }}
              >
                <PersonAddIcon />
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  {app.displayName || 'N/A'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {app.email}
                </Typography>
                <Box sx={{ mt: 1 }}>
                  <Chip
                    label="Pending Review"
                    color="warning"
                    size="small"
                  />
                </Box>
              </Box>
              <IconButton onClick={() => setExpanded(!expanded)} size="small">
                {expanded ? <CloseIcon /> : <ViewIcon />}
              </IconButton>
            </Stack>
            
            {expanded && (
              <Fade in={expanded}>
                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Stack spacing={2}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">{app.email}</Typography>
                    </Stack>
                    {app.phone && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{app.phone}</Typography>
                      </Stack>
                    )}
                    {app.address && (
                      <Stack direction="row" alignItems="center" spacing={1}>
                        <LocationOnIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                        <Typography variant="body2">{app.address}</Typography>
                      </Stack>
                    )}
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <ScheduleIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                      <Typography variant="body2">
                        Applied: {new Date(app.createdAt).toLocaleDateString()}
                      </Typography>
                    </Stack>
                    
                    <Divider />
                    
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        variant="outlined"
                        color="info"
                        startIcon={<ViewIcon />}
                        onClick={() => viewDetails(app)}
                      >
                        Details
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon />}
                        onClick={() => handleApprove(app)}
                        disabled={actionLoading}
                      >
                        Approve
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        color="error"
                        startIcon={<RejectIcon />}
                        onClick={() => handleReject(app)}
                        disabled={actionLoading}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Fade>
            )}
          </CardContent>
        </Card>
      </Zoom>
    );
  };

  if (!isSuperAdmin) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          Access denied. Super Admin only.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ pb: { xs: 2, sm: 3 } }}>


      {/* Success/Error Messages */}
      <Fade in={!!success || !!error}>
        <Stack spacing={2} sx={{ mb: 3 }}>
          {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ borderRadius: 2 }}>{success}</Alert>}
          {error && <Alert severity="error" onClose={() => setError('')} sx={{ borderRadius: 2 }}>{error}</Alert>}
        </Stack>
      </Fade>

      {/* Stats Cards - Horizontal Scroll on Mobile */}
      <Box sx={{ 
        overflowX: 'auto', 
        mb: 4,
        pb: 1,
        '&::-webkit-scrollbar': {
          height: 4,
        },
        '&::-webkit-scrollbar-track': {
          bgcolor: 'grey.100',
          borderRadius: 2,
        },
        '&::-webkit-scrollbar-thumb': {
          bgcolor: 'grey.400',
          borderRadius: 2,
        },
      }}>
        <Stack direction="row" spacing={2} sx={{ minWidth: 'min-content' }}>
          <Card sx={{ borderRadius: 2, minWidth: { xs: 140, sm: 160 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: { xs: 10, sm: 11 } }}>
                    Total Applications
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {stats.total}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: alpha(theme.palette.info.main, 0.1), borderRadius: 2, p: 1 }}>
                  <PersonAddIcon sx={{ color: theme.palette.info.main, fontSize: { xs: 20, sm: 28 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>

          <Card sx={{ borderRadius: 2, minWidth: { xs: 140, sm: 160 }, flexShrink: 0 }}>
            <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between">
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontWeight: 600, fontSize: { xs: 10, sm: 11 } }}>
                    Pending Review
                  </Typography>
                  <Typography variant="h5" sx={{ fontWeight: 700, mt: 0.5, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                    {stats.pending}
                  </Typography>
                </Box>
                <Box sx={{ bgcolor: alpha(theme.palette.warning.main, 0.1), borderRadius: 2, p: 1 }}>
                  <ScheduleIcon sx={{ color: theme.palette.warning.main, fontSize: { xs: 20, sm: 28 } }} />
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Stack>
      </Box>

      {/* Applications Table/Cards */}
      <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {/* Header with Refresh */}
        <Box sx={{ 
          p: 2, 
          borderBottom: 1, 
          borderColor: 'divider',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1,
        }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            Pending Applications
          </Typography>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={fetchApplications}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ p: 3 }}>
            <Stack spacing={2}>
              {[1, 2, 3].map((_, idx) => (
                <Skeleton key={idx} variant="rectangular" height={80} sx={{ borderRadius: 2 }} />
              ))}
            </Stack>
          </Box>
        ) : applications.length === 0 ? (
          <Box sx={{ p: 8, textAlign: 'center' }}>
            <PersonAddIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
            <Typography color="text.secondary">No pending applications</Typography>
            <Typography variant="caption" color="text.secondary">
              All administrator applications have been processed
            </Typography>
          </Box>
        ) : (
          <>
            {/* Desktop Table View */}
            {!isMobile && (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                      <TableCell sx={{ fontWeight: 600 }}>Applicant</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Contact</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Applied Date</TableCell>
                      <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {applications.map((app, index) => (
                      <TableRow key={app.id} hover>
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={2}>
                            <Avatar sx={{ bgcolor: theme.palette.warning.main, width: 40, height: 40 }}>
                              {app.displayName?.[0] || app.email?.[0]}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                                {app.displayName || 'N/A'}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {app.email}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {app.phone && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <PhoneIcon sx={{ fontSize: 12 }} /> {app.phone}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {new Date(app.createdAt).toLocaleDateString()}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {new Date(app.createdAt).toLocaleTimeString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label="Pending Review"
                            color="warning"
                            size="small"
                            icon={<ScheduleIcon sx={{ fontSize: 14 }} />}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Stack direction="row" spacing={1} justifyContent="center">
                            <Tooltip title="View Details">
                              <Button
                                size="small"
                                variant="outlined"
                                color="info"
                                startIcon={<ViewIcon />}
                                onClick={() => viewDetails(app)}
                                sx={{ textTransform: 'none' }}
                              >
                                {!isTablet && "View"}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Approve Application">
                              <Button
                                size="small"
                                variant="contained"
                                color="success"
                                startIcon={<ApproveIcon />}
                                onClick={() => handleApprove(app)}
                                disabled={actionLoading}
                                sx={{ textTransform: 'none' }}
                              >
                                {!isTablet && "Approve"}
                              </Button>
                            </Tooltip>
                            <Tooltip title="Reject Application">
                              <Button
                                size="small"
                                variant="contained"
                                color="error"
                                startIcon={<RejectIcon />}
                                onClick={() => handleReject(app)}
                                disabled={actionLoading}
                                sx={{ textTransform: 'none' }}
                              >
                                {!isTablet && "Reject"}
                              </Button>
                            </Tooltip>
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Mobile Card View */}
            {isMobile && (
              <Box sx={{ p: 2 }}>
                {applications.map((app, index) => (
                  <ApplicationCard key={app.id} app={app} index={index} />
                ))}
              </Box>
            )}
          </>
        )}
      </Paper>

      {/* Application Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">Application Details</Typography>
            <IconButton onClick={() => setDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          {selectedApp && (
            <Stack spacing={3}>
              <Stack alignItems="center" spacing={2}>
                <Avatar 
                  sx={{ 
                    width: 80, 
                    height: 80, 
                    bgcolor: theme.palette.warning.main,
                    fontSize: 32,
                  }}
                >
                  {selectedApp.displayName?.[0] || selectedApp.email?.[0]}
                </Avatar>
                <Typography variant="h6">{selectedApp.displayName}</Typography>
                <Chip label="Pending Review" color="warning" size="small" icon={<ScheduleIcon />} />
              </Stack>

              <Divider />

              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">Email Address</Typography>
                  <Typography variant="body2">{selectedApp.email}</Typography>
                </Stack>
                
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">Phone Number</Typography>
                  <Typography variant="body2">{selectedApp.phone || 'Not provided'}</Typography>
                </Stack>
                
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                  <Typography variant="body2">{selectedApp.address || 'Not provided'}</Typography>
                </Stack>
                
                <Stack direction="row" justifyContent="space-between" flexWrap="wrap" gap={1}>
                  <Typography variant="subtitle2" color="text.secondary">Application Date</Typography>
                  <Typography variant="body2">{new Date(selectedApp.createdAt).toLocaleString()}</Typography>
                </Stack>
              </Stack>

              <Divider />

              <Stack direction="row" spacing={2} justifyContent="flex-end">
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<ViewIcon />}
                  onClick={() => setDialogOpen(false)}
                >
                  Close
                </Button>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<ApproveIcon />}
                  onClick={() => {
                    handleApprove(selectedApp);
                    setDialogOpen(false);
                  }}
                  disabled={actionLoading}
                >
                  Approve
                </Button>
                <Button
                  variant="contained"
                  color="error"
                  startIcon={<RejectIcon />}
                  onClick={() => {
                    handleReject(selectedApp);
                    setDialogOpen(false);
                  }}
                  disabled={actionLoading}
                >
                  Reject
                </Button>
              </Stack>
            </Stack>
          )}
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default AdminApprovals;